<template>
  <div class="stack-info-container">
    <!-- column left -->
    <div class="stack-info__column-left">
      <div class="stack-info__image mb-4 d-flex">
        <BasePresignedImage
          :default-src="stack.image.url"
          :file-id="stack.image.id"
          :public="true"
          class="img-fluid w-100"
        />
      </div>
      <div class="stack-info__content">
        <h2 class="text__stack-title font-weight-bold mb-u5">{{ stack.title }}</h2>
        <p v-safe-html="stack.description" class="text__stack-description"></p>
      </div>
    </div>

    <!--column right -->
    <div class="stack-info__column-right">
      <div class="stack-info__card">
        <div class="stack-info__card-header">
          <div>
            <h4 class="font-weight-bold">
              <template v-if="hasUserAllAwardedLearningUnits">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="currentColor"
                >
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM17.4571 9.45711L11 15.9142L6.79289 11.7071L8.20711 10.2929L11 13.0858L16.0429 8.04289L17.4571 9.45711Z"
                  ></path>
                </svg>
                You’ve earned all skills!
              </template>
              <template v-else>
                {{ titleText }}
              </template>
            </h4>
            <p>The learner provides intellectual stimulation in the workplace.</p>
          </div>
          <div>
            <b-spinner v-if="isRedeeming" small label="Redeeming..." />
            <div v-else class="button__redeem-code-container">
              <b-button
                class="button__verify-or-enroll-now"
                variant="warning"
                @click="OnClickEnRoll()"
              >
                {{ buttonText }}
              </b-button>

              <div>
                <button
                  v-if="allowToRedeemCode"
                  type="button"
                  variant="link"
                  class="btn btn-link button__redeem-code"
                  :disabled="isIssuer || isReviewer"
                  @click="onClickRedeemCode()"
                >
                  Have a redeem code?
                </button>
                <a
                  v-if="isIssuer || isReviewer"
                  ref="tooltip"
                  v-b-tooltip.hover.bottom.v-light
                  class="icon__cannnot-apply-stack-tool-tip"
                  :title="`You can't buy or apply this stack because you are ${
                    isIssuer ? 'issuer' : 'reviewer'
                  }.`"
                >
                  <font-awesome-icon icon="exclamation-circle" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="stack-info__list">
        <div>
          <h5 class="mb-3 font-weight-bold">What’s included</h5>
        </div>
        <div class="stack-info__card-body font-weight-bold">
          <li
            v-for="(benefit, index) in includedBenefits"
            :key="benefit"
            class="py-2 d-flex flex-row align-items-baseline"
          >
            <div>
              <b-img
                :ref="`icon__card-mc-learn-verify-${index}`"
                :title="`IconCardLearnVerify_${index}`"
                class="mr-2"
                :src="srcIcon[index]"
              ></b-img>
            </div>
            <div>{{ benefit }}</div>
          </li>
        </div>
        <p class="text-black-50 pl-u7 mb-0">
          *For
          <span class="font-weight-bold"> Learn + Verify </span>
          plan only.
        </p>
      </div>
    </div>
    <redeem-code-modal
      :show.sync="activeModalRedeem"
      @redeem-success:hide="onCloseModalRedeem"
      @on:hide="isRedeeming = false"
    ></redeem-code-modal>
  </div>
</template>

<script>
import BasePresignedImage from '@/components/base-components/BasePresignedImage.vue';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import RedeemCodeModal from '@/components/LearningUnit/RedeemCodeModal.vue';

export default {
  name: 'PageStackHeaderInfo',

  components: {
    BasePresignedImage,
    RedeemCodeModal
  },

  mixins: [userInfoMixin],

  props: {
    stack: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeModalRedeem: false, // Redeem code modal
      isRedeeming: false // Redeeming state
    };
  },
  computed: {
    srcIcon() {
      return [
        require('@/assets/icon-card-mc-detail/blue/icon_card_b_1.png'),
        require('@/assets/icon-card-mc-detail/blue/icon_card_b_2.png'),
        require('@/assets/icon-card-mc-detail/blue/icon_card_b_3.png'),
        require('@/assets/icon-card-mc-detail/blue/icon_card_b_4.png'),
        require('@/assets/icon-card-mc-detail/blue/icon_card_b_5.png'),
        require('@/assets/icon-card-mc-detail/blue/icon_card_b_7.png'),
        require('@/assets/icon-card-mc-detail/blue/icon_card_b_8.png')
      ];
    },
    includedBenefits() {
      return [
        'Unlimited Access to Competency Guidelines',
        '2 Submission Quotas to Verify Competency',
        'Personalized Feedback',
        'Digital Badge',
        'Shareable to Social Network',
        'Online Learning Videos from Expert*',
        'Interactive Expert Community*'
      ];
    },

    // from get stack detail
    learningUnits() {
      return this.stack.learning_units || [];
    },

    // User role checks
    isReviewer() {
      return this.$currentUser?.isReviewer();
    },

    isIssuer() {
      return this.$currentUser?.isIssuer();
    },

    //  Check the relevant learning units where the user is not the owner and assessor.
    relevantUnits() {
      if (!this.$currentUser) return [];

      return this.learningUnits.filter(
        (unit) =>
          !this.$currentUser?.isOwnerOf?.(unit.id) && !this.$currentUser?.isAssessorOf?.(unit.id)
      );
    },

    // Check if user has applied all learning units
    hasUserAppliedAllLearningUnits() {
      return this.relevantUnits.length > 0 && this.relevantUnits.every((unit) => unit?.is_applied);
    },

    // Check if user has applied some learning units
    hasUserAppliedSomeStack() {
      return this.relevantUnits.length > 0 && this.relevantUnits.some((unit) => unit?.is_applied);
    },

    // Check if user has all awarded learning units
    hasUserAllAwardedLearningUnits() {
      const sameLength = this.learningUnits.length === this.relevantUnits.length; // Ensure both arrays have the same length

      return (
        sameLength &&
        this.relevantUnits.length > 0 &&
        this.relevantUnits.every((unit) => unit?.is_awarded)
      );
    },

    buttonText() {
      if (this.hasUserAllAwardedLearningUnits) return 'View Your Skills';
      if (this.hasUserAppliedAllLearningUnits) return 'Verify Now';
      return 'Enroll Now';
    },

    titleText() {
      if (this.hasUserAppliedAllLearningUnits) return 'Continue to verify your skill';
      if (this.hasUserAppliedSomeStack) return 'Continue to earn your skill';
      return 'Start to earn first skill';
    },

    // Allow user to redeem code without owning or assessing any unit
    allowToRedeemCode() {
      if (this.hasAllAwardedLearningUnits || this.hasAppliedAllLearningUnits) return false;

      const canRedeem = this.learningUnits.some(
        (unit) =>
          !unit.is_applied &&
          !this.$currentUser?.isOwnerOf?.(unit.id) &&
          !this.$currentUser?.isAssessorOf?.(unit.id)
      );

      return canRedeem;
    }
  },
  methods: {
    OnClickEnRoll() {
      this.$emit(this.user ? 'enroll' : 'login');
    },

    onClickRedeemCode() {
      if (!this.$currentUser) return this.goToSignInPageWithRedirect();

      this.activeModalRedeem = true;
    },

    async onCloseModalRedeem() {
      this.activeModalRedeem = false;

      await this.refreshStackData();
    },

    goToSignInPageWithRedirect() {
      const previousRoute = this.$router.resolve({
        ...this.$route,
        params: {
          stackId: this.stack.id
        }
      });

      this.$router.push({
        name: 'SignIn',
        query: {
          previousPath: previousRoute.href
        }
      });
    },

    async refreshStackData() {
      this.isRedeeming = true;

      this.$emit('refreshStackData', this.stack.id);

      this.isRedeeming = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.stack-info-container {
  display: grid;
  grid-template-columns: 1fr; //default
  gap: 40px;
}

.stack-info__column-left {
  width: 100%;
}

.stack-info__image img {
  width: 100%;
  height: auto;
  border-radius: 12px;
}

.stack-info__card {
  background: linear-gradient(91.61deg, #667ce8 0%, #744ba3 100%);
  color: #ffffff;
  border-radius: 10.4px;

  &-header {
    padding: 24px;
  }
}

.stack-info__list {
  padding: 24px;
  background: #f8f9ff;
}

.stack-info__card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;

  p {
    margin: 0;
  }

  .button__redeem-code {
    color: #ffffff;
    text-decoration: underline;
    padding: 0;
    margin-right: 14px;
  }

  .button__redeem-code-container {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }
}

::v-deep .tooltip-inner {
  max-width: 260px;
}

.tooltip.b-tooltip {
  opacity: 1;
}

@media screen and (min-width: 576px) {
  .stack-info__card-header {
    flex-wrap: nowrap;
    gap: 16px;

    p {
      max-width: 311px;
    }
  }
}

@media screen and (min-width: 768px) {
  .stack-info__column-left {
    max-width: none;
  }
  .stack-info__content {
    h2 {
      font-size: 40px;
    }
    h5 {
      font-size: 24px;
    }
  }

  .stack-info__card-header {
    flex-wrap: wrap;
    p {
      max-width: none;
    }
  }
}

@media screen and (min-width: 992px) {
  .stack-info-container {
    grid-template-columns: minmax(539px, 2fr) minmax(435px, 1fr);
  }
}

@media screen and (min-width: 1200px) {
  .stack-info-container {
    grid-template-columns: minmax(625px, 2fr) minmax(435px, 1fr);
  }
}
</style>
